import * as React from 'react'
import { cn } from '@/lib/utils'
import { TextareaAiAddon, type AiFieldKind } from './improve-with-ai'

interface Props extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  /** How far it grows before becoming a scrollable area. */
  maxHeight?: number
  /**
   * Height floor. Used to pair two fields side by side: each one reports the
   * height it would need and gets back the larger of the two.
   */
  minHeight?: number
  /** The height the content would ask for on its own, ignoring `minHeight`. */
  onMeasure?: (height: number) => void
  /** F-105: shows "Improve with AI" on this field (long, free text only). */
  aiFieldKind?: AiFieldKind
}

/**
 * A textarea that starts at one line and grows as you type.
 *
 * It exists for fields in a fixed footer: reserving four lines of height for a
 * comment that is usually one line steals reading space all the time, and four
 * lines are also too few when someone really wants to write.
 */
export const AutoTextarea = React.forwardRef<HTMLTextAreaElement, Props>(
  ({ className, maxHeight = 160, minHeight = 0, value, onChange, onMeasure, aiFieldKind, ...props }, forwarded) => {
    const inner = React.useRef<HTMLTextAreaElement | null>(null)

    const resize = React.useCallback((el: HTMLTextAreaElement | null) => {
      if (!el) return
      // reset before measuring, otherwise scrollHeight never shrinks on delete
      el.style.height = 'auto'
      /* The natural height is measured without the floor — it is what the pair
         uses to agree. With `minHeight` here, one field would push the other
         forever. */
      const natural = Math.min(el.scrollHeight, maxHeight)
      el.style.height = `${Math.max(natural, Math.min(minHeight, maxHeight))}px`
      el.style.overflowY = el.scrollHeight > maxHeight ? 'auto' : 'hidden'
      onMeasure?.(natural)
    }, [maxHeight, minHeight, onMeasure])

    /** Also on switching submissions, when the value changes with no typing. */
    React.useEffect(() => { resize(inner.current) }, [value, resize])

    const textarea = (
      <textarea
        ref={(el) => {
          inner.current = el
          if (typeof forwarded === 'function') forwarded(el)
          else if (forwarded) forwarded.current = el
        }}
        rows={1}
        value={value}
        onChange={(e) => { resize(e.currentTarget); onChange?.(e) }}
        className={cn(
          'flex w-full resize-none rounded-md border border-input bg-transparent px-3 py-2 text-sm',
          'shadow-sm transition-colors placeholder:text-muted-foreground',
          'focus-visible:border-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-200',
          'disabled:cursor-not-allowed disabled:opacity-50',
          aiFieldKind && 'pr-11',
          className
        )}
        {...props}
      />
    )
    if (!aiFieldKind) return textarea
    return (
      <div className="relative w-full min-w-0">
        {textarea}
        <TextareaAiAddon target={inner} value={value} fieldKind={aiFieldKind} maxLength={props.maxLength}
          disabled={props.disabled || props.readOnly} compact className="absolute bottom-0.5 right-1" />
      </div>
    )
  }
)
AutoTextarea.displayName = 'AutoTextarea'
